import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { fmtDate, toInputDate, fromInputDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import ChaserComposer from "@/components/ChaserComposer";

export default function JobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [contacts, setContacts] = useState([]);

  const load = useCallback(async () => {
    const [j, t, r, n] = await Promise.all([
      api.get(`/jobs/${jobId}`),
      api.get(`/tasks`, { params: { job_id: jobId } }),
      api.get(`/reminders`),
      api.get(`/notes`, { params: { job_id: jobId } }),
    ]);
    setJob(j.data);
    setTasks(t.data);
    setReminders(r.data.filter((x) => x.job_id === jobId));
    setNotes(n.data);
    // load contacts for chaser composer
    const ct = await api.get(`/clients/${j.data.client_id}/contacts`);
    setContacts(ct.data);
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.patch(`/jobs/${jobId}`, {
        title: job.title,
        stage: job.stage,
        status: job.status,
        deadline: job.deadline,
        reminders_paused: job.reminders_paused,
      });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    await api.post("/tasks", { job_id: jobId, title: newTask });
    setNewTask("");
    load();
  };

  const toggleTask = async (id) => {
    await api.patch(`/tasks/${id}`);
    load();
  };

  const delTask = async (id) => {
    await api.delete(`/tasks/${id}`);
    load();
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteBody.trim()) return;
    await api.post("/notes", { client_id: job.client_id, job_id: jobId, body: noteBody });
    setNoteBody("");
    load();
  };

  if (!job) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <PageHeader
        testid="page-job-detail"
        title={job.title}
        subtitle={<>Client: <Link to={`/clients/${job.client_id}`} className="text-blue-600 hover:underline">{job.client_name}</Link></>}
        actions={
          <div className="flex gap-2">
            <StatusBadge status={job.status} />
            <ChaserComposer clientId={job.client_id} jobId={jobId} contacts={contacts} onCreated={load} />
            <Button data-testid="save-job-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800"><Save className="h-4 w-4 mr-2" /> Save changes</Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: main */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-4">
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Job details</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={job.title} onChange={(e) => setJob({ ...job, title: e.target.value })} />
              </div>
              <div>
                <Label>Stage</Label>
                <Select value={job.stage} onValueChange={(v) => setJob({ ...job, stage: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(job.stages || ["Awaiting records", "In progress", "Client review", "Filed"]).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={job.status} onValueChange={(v) => setJob({ ...job, status: v })}>
                  <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not started</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="on_hold">On hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Deadline · <span className="text-amber-700">editable</span></Label>
                <Input type="date" value={toInputDate(job.deadline)} onChange={(e) => setJob({ ...job, deadline: fromInputDate(e.target.value) })} />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch data-testid="pause-reminders-switch" checked={!!job.reminders_paused} onCheckedChange={(v) => setJob({ ...job, reminders_paused: v })} />
              Pause reminders on this job
            </label>
          </div>

          <div className="border border-slate-200 rounded-md bg-white">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Tasks · {tasks.filter(t => t.status === "open").length} open</div>
              <form onSubmit={addTask} className="flex gap-2">
                <Input data-testid="task-input" placeholder="Add task…" value={newTask} onChange={(e) => setNewTask(e.target.value)} className="w-64 h-8" />
                <Button data-testid="add-task-btn" type="submit" size="sm" className="bg-slate-900 hover:bg-slate-800"><Plus className="h-3.5 w-3.5" /></Button>
              </form>
            </div>
            <ul className="divide-y divide-slate-100">
              {tasks.length === 0 && <li className="px-5 py-6 text-sm text-slate-500 text-center">No tasks yet.</li>}
              {tasks.map((t) => (
                <li key={t.task_id} className="px-5 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <Checkbox data-testid="task-checkbox" checked={t.status === "done"} onCheckedChange={() => toggleTask(t.task_id)} />
                  <span className={`text-sm flex-1 ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</span>
                  <button onClick={() => delTask(t.task_id)} className="text-slate-400 hover:text-rose-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-slate-200 rounded-md bg-white">
            <div className="px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Notes</div>
            <form onSubmit={addNote} className="p-4 space-y-2 border-b border-slate-100">
              <Textarea placeholder="Add note for this job…" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} />
              <div className="text-right"><Button type="submit" size="sm" className="bg-slate-900 hover:bg-slate-800">Post</Button></div>
            </form>
            <ul className="divide-y divide-slate-100">
              {notes.map((n) => (
                <li key={n.note_id} className="px-5 py-3">
                  <div className="text-xs text-slate-500 mb-1 flex justify-between"><span>{n.author_name}</span><span className="font-mono">{fmtDateTime(n.created_at)}</span></div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</div>
                </li>
              ))}
              {notes.length === 0 && <li className="px-5 py-6 text-sm text-slate-500 text-center">No notes yet.</li>}
            </ul>
          </div>
        </div>

        {/* Right: reminders */}
        <div className="space-y-4">
          <div className="border border-slate-200 rounded-md bg-white">
            <div className="px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Scheduled reminders</div>
            <ul className="divide-y divide-slate-100">
              {reminders.length === 0 && <li className="px-5 py-6 text-sm text-slate-500 text-center">None queued.</li>}
              {reminders.map((r) => (
                <li key={r.reminder_id} className="px-5 py-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-800">T-{r.trigger_days_before} days</div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-1">{fmtDateTime(r.scheduled_at)}</div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{r.subject}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
